import { useEffect, useMemo } from 'react';

import {
  TravellerWithCollectionMembershipsDTO,
  useGetDepartmentsList,
  useListPayers,
} from '@/api/trafikportalen';
import { FormattedMessage, useIntl } from 'react-intl';

import { hasDepartmentWriteAccess } from '@/helpers/departmentPermissions';
import { getDepartmentPayer } from '@/helpers/departmentPayer';
import { FormSection } from '../FormSection';
import { SelectableList } from '../SelectableList';
import Input from '../core/Input';
import { useGetDepartmentDisplayName } from '@/hooks/useGetStudentCollection';

interface Props {
  selectedTraveller?: TravellerWithCollectionMembershipsDTO;
  reason: string;
  setReason: (reason: string) => void;
  selectedDepartmentId: string;
  setDepartmentId: (departmentId: string) => void;
  isTemporaryTraveller?: boolean;
}

export const PaymentSelection = ({
  selectedTraveller,
  reason,
  setReason,
  selectedDepartmentId,
  setDepartmentId,
  isTemporaryTraveller = false,
}: Props) => {
  const { formatMessage } = useIntl();

  const getDepartmentDisplayName = useGetDepartmentDisplayName();

  const { data: departments, isLoading: isDepartmentsLoading } =
    useGetDepartmentsList();

  const { data: payers } = useListPayers();

  const hasMembershipWithWriteAccess = useMemo(() => {
    if (!selectedTraveller) return false;
    return selectedTraveller.memberships.some(
      (m) => m.active && hasDepartmentWriteAccess(m.department_id),
    );
  }, [selectedTraveller]);

  const validDepartments = useMemo(() => {
    if (!departments) return [];
    if (!isTemporaryTraveller && !selectedTraveller) return [];

    if (isTemporaryTraveller || !hasMembershipWithWriteAccess) {
      return departments.filter((department) =>
        hasDepartmentWriteAccess(department.id),
      );
    }

    return departments.filter(
      (department) =>
        hasDepartmentWriteAccess(department.id) &&
        selectedTraveller!.memberships.some(
          (m) => m.department_id === department.id && m.active,
        ),
    );
  }, [
    departments,
    selectedTraveller,
    isTemporaryTraveller,
    hasMembershipWithWriteAccess,
  ]);

  useEffect(() => {
    if (validDepartments.length > 0) {
      setDepartmentId(validDepartments[0].id);
    }
  }, [validDepartments, setDepartmentId]);

  const departmentOptions = useMemo(() => {
    if (!departments || !validDepartments) {
      return [];
    }

    return validDepartments.map((department) => {
      const payer = getDepartmentPayer(department, departments, payers);

      return {
        id: department.id,
        label: `${getDepartmentDisplayName(department.id)} ${payer ? `(${payer.reference})` : ''}`,
      };
    });
  }, [departments, getDepartmentDisplayName, payers, validDepartments]);

  if (
    selectedTraveller &&
    validDepartments.length === 0 &&
    !isDepartmentsLoading
  ) {
    return (
      <FormSection
        title={formatMessage({ defaultMessage: 'Betalningsinformation' })}
      >
        <i className="font-bold text-red-700">
          <FormattedMessage defaultMessage="Du har inte skrivrättighet i någon avdelning som stödjer biljettköp." />
        </i>
      </FormSection>
    );
  }

  return (
    <FormSection
      title={formatMessage({ defaultMessage: 'Betalningsinformation' })}
    >
      <Input
        id="notation"
        type="text"
        label={formatMessage({ defaultMessage: 'Ange syfte med resan' })}
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        maxLength={50}
        className="mb-4"
        required
      />
      <p className="text-text-900">
        <FormattedMessage defaultMessage="Välj avdelning (och referens) för betalning." />{' '}
        {hasMembershipWithWriteAccess && !isTemporaryTraveller ? (
          <FormattedMessage defaultMessage="Går enbart att välja avdelningar som resenären är medlem i." />
        ) : null}
      </p>
      <SelectableList
        options={departmentOptions}
        selectedOption={selectedDepartmentId}
        setSelectedOption={setDepartmentId}
        className="h-44"
        noResultsMessage={
          isTemporaryTraveller
            ? formatMessage({
                defaultMessage: 'Inga tillgängliga avdelningar',
              })
            : formatMessage({
                defaultMessage:
                  'Välj en resenär för att se tillgängliga avdelningar',
              })
        }
      />
    </FormSection>
  );
};
